import * as validator from 'validator';
import { z } from 'zod';

import { carrier, isInCarrierPool, simSlot } from './carriers';

const imei = z
  .string()
  .refine((v) => validator.isIMEI(v, { allow_hyphens: true }), {
    message: 'IMEI is not valid',
  });

export const ICCID_PATTERN = /^\d{18,22}$/;
export const SIM_SLOTS_PER_ROUTER = 2;

export const routerSimBody = z
  .object({
    slot: simSlot,
    carrier,
    iccid: z.string().regex(ICCID_PATTERN, 'ICCID must be 18 to 22 digits'),
    ipAddress: z.ipv4(),
    apn: z.string().min(1).optional(),
    isPrimary: z.boolean(),
  })
  .refine((sim) => isInCarrierPool(sim.carrier, sim.ipAddress), {
    path: ['ipAddress'],
    message: 'IP address is outside the carrier address pool',
  });
export type RouterSimBody = z.infer<typeof routerSimBody>;

const routerSims = z
  .array(routerSimBody)
  .min(1, 'Add at least one SIM')
  .max(SIM_SLOTS_PER_ROUTER)
  .refine((sims) => new Set(sims.map((sim) => sim.slot)).size === sims.length, {
    message: 'Each SIM slot can only be used once',
  })
  .refine((sims) => new Set(sims.map((sim) => sim.iccid)).size === sims.length, {
    message: 'Each SIM must have a different ICCID',
  })
  .refine((sims) => sims.filter((sim) => sim.isPrimary).length === 1, {
    message: 'Exactly one SIM must be primary',
  });

export const createRouterBody = z.object({
  name: z.string().min(1),
  imei,
  username: z.string().min(1),
  password: z.string().min(1),
  patientId: z.uuid().optional(),
  cameraIds: z.array(z.uuid()).optional(),
  nvrIds: z.array(z.uuid()).optional(),
  sims: routerSims,
});
export type CreateRouterBody = z.infer<typeof createRouterBody>;

export const updateRouterBody = createRouterBody.extend({
  id: z.uuid(),
});
export type UpdateRouterBody = z.infer<typeof updateRouterBody>;

export const routerIdParam = z.object({ id: z.string().min(1) });
export type RouterIdParam = z.infer<typeof routerIdParam>;
